/**
 * Reusable click-outside hook for dropdown menus
 *
 * Closes a dropdown when the user presses outside the element(s) matching
 * the given CSS selector(s). Uses document-level click listeners on web,
 * which reliably detect clicks regardless of stacking context or overflow.
 * This replaces the fragile "fixed inset-0 Pressable" backdrop pattern
 * that fails inside Modals.
 * @module hooks/useClickOutside
 */

import { useEffect } from "react";
import { Platform } from "react-native";

/**
 * Listens for clicks outside the specified data-attribute selectors and
 * calls onClose when detected. Web-only — no-ops on native.
 * @param isOpen - Whether the dropdown is currently open
 * @param selectors - CSS selector string(s) that identify the dropdown area (e.g., "[data-sort-dropdown]")
 * @param onClose - Callback to close the dropdown
 * @param eventName - Outside event to observe; defaults to click for keyboard compatibility
 */
const useClickOutside = (
  isOpen: boolean,
  selectors: string,
  onClose: () => void,
  eventName: "click" | "pointerdown" = "click"
): void => {
  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen) return undefined;

    const handleOutside = (event: Event) => {
      const target = event.target;
      const element =
        target instanceof Element
          ? target
          : ((target as Node | null)?.parentElement ?? null);
      if (!element?.closest(selectors)) {
        onClose();
      }
    };

    document.addEventListener(eventName, handleOutside); // NOSONAR — intentional document-level outside interaction detection
    return () => document.removeEventListener(eventName, handleOutside);
  }, [eventName, isOpen, selectors, onClose]);
};

export default useClickOutside;
